import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  type EnforceDisabledReason,
  PolicyMode,
  type PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import { PolicyModeSelector } from '../../components/PolicyModeSelector';
import type { AllowBlockPolicyKey, PolicyRule } from '../types';
import { getAllowBlockUiConfig } from '../config';
import { PolicyRuleList } from './PolicyRuleList';

export interface AllowBlockSectionProps {
  policyKey: AllowBlockPolicyKey;
  variant: 'allowed' | 'blocked';
  rules: PolicyRule[];
  /** Saved snapshot rows for this list (diff + soft-delete restore). */
  baselineRules: PolicyRule[];
  onRulesChange: (next: PolicyRule[]) => void;
  readOnly: boolean;
  /** Support role: rows stay viewable/expandable, only commit actions are disabled.
   *  Does not affect the blocked-list mode selector, which stays plain-disabled
   *  for support like other boolean/string settings. */
  isSupportReadOnly?: boolean;
  /** Blocked section only */
  blockedListMode?: PolicyModeType;
  onBlockedListModeChange?: (mode: PolicyModeType) => void;
  /** Blocked: tri-state differs from last save. */
  blockedModeDirty?: boolean;
  supportsEnforce?: boolean;
  enforceDisabledReason?: EnforceDisabledReason;
  enforceTooltip?: string;
  onRuleListValidationChange?: (valid: boolean) => void;
}

export function AllowBlockSection({
  policyKey,
  variant,
  rules,
  baselineRules,
  onRulesChange,
  readOnly,
  isSupportReadOnly = false,
  blockedListMode,
  onBlockedListModeChange,
  blockedModeDirty = false,
  supportsEnforce = true,
  enforceDisabledReason,
  enforceTooltip,
  onRuleListValidationChange,
}: AllowBlockSectionProps): ReactElement {
  const activeRuleCount = rules.filter(r => !r.pendingDelete).length;
  const cfg = getAllowBlockUiConfig(policyKey);
  const title =
    variant === 'allowed' ? cfg.allowedSectionTitle : cfg.blockedSectionTitle;
  const note = variant === 'allowed' ? cfg.allowedNote : cfg.blockedNote;
  const tooltip =
    variant === 'allowed'
      ? cfg.allowedSectionTooltip
      : cfg.blockedSectionTooltip;

  return (
    <div
      data-testid={`allow-block-section-${policyKey}-${variant}`}
      className="rounded-lg border bg-card dark:bg-surface-elevated dark:border-surface-elevated-border p-4 space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold flex items-center gap-2 flex-wrap">
            <span>{title}</span>
            <Badge
              variant="secondary"
              className="tabular-nums h-5 px-1.5 text-xs font-normal"
              aria-label={`${activeRuleCount} ${variant === 'allowed' ? 'allowed' : 'blocked'} rules`}
            >
              {activeRuleCount}
            </Badge>
          </h3>
          {tooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Section info"
                >
                  <Info className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                sideOffset={6}
                className="max-w-md whitespace-normal leading-snug antialiased"
              >
                {tooltip}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground cursor-default">
                  <Info className="size-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                sideOffset={6}
                className="max-w-md whitespace-normal leading-snug antialiased"
              >
                {variant === 'allowed'
                  ? 'Rules explicitly allowed for this policy.'
                  : 'Rules explicitly blocked for this policy.'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {variant === 'blocked' &&
          blockedListMode != null &&
          onBlockedListModeChange != null && (
            <div
              className={cn(
                'w-[min(100%,280px)] shrink-0 rounded-md p-0.5 transition-[box-shadow]',
                blockedModeDirty &&
                  'ring-2 ring-amber-500/55 ring-offset-2 ring-offset-card'
              )}
            >
              <PolicyModeSelector
                value={blockedListMode}
                onChange={onBlockedListModeChange}
                disabled={readOnly}
                supportsEnforce={supportsEnforce}
                enforceDisabledReason={enforceDisabledReason}
                enforceTooltip={enforceTooltip}
              />
            </div>
          )}
      </div>
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Note:</span> {note}
      </p>
      <div
        className={cn(
          'relative transition-opacity',
          variant === 'blocked' &&
            blockedListMode === PolicyMode.Off &&
            'pointer-events-none select-none'
        )}
      >
        {variant === 'blocked' && blockedListMode === PolicyMode.Off && (
          <>
            <div className="absolute inset-0 rounded-md bg-background/35 dark:bg-background/45 z-10" />
            <div
              className="pointer-events-none absolute top-2 right-2 z-20 rounded-md border border-border bg-card/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground shadow-sm"
              aria-hidden
            >
              Off
            </div>
          </>
        )}
        <div
          className={cn(
            variant === 'blocked' &&
              blockedListMode === PolicyMode.Off &&
              'opacity-60 saturate-75'
          )}
        >
          <PolicyRuleList
            policyKey={policyKey}
            rules={rules}
            baselineRules={baselineRules}
            onRulesChange={onRulesChange}
            readOnly={readOnly || blockedListMode === PolicyMode.Off}
            // Off-mode locks the table for everyone; don't let the support
            // relaxation re-enable row expansion (keyboard focus/Enter would
            // bypass the `pointer-events-none` wrapper, which only blocks mouse).
            isSupportReadOnly={
              isSupportReadOnly && blockedListMode !== PolicyMode.Off
            }
            onValidationChange={onRuleListValidationChange}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{cfg.footerAndLogicText}</p>
    </div>
  );
}
